import React, { useCallback, useMemo, useRef } from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import {
  HMDFeedback as SUHMDFeedback,
  FeedbackOptions,
  ThumbFeedbackType,
} from '@amzn/seller-university-ui-components';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, HMD_FEEDBACK_STRING_ID } from 'src/constants/localization';
import { useAddContentFeedbackMutation } from 'src/redux/slices/api/su';
import { useClientId, UNGATED_CLIENT_ID } from 'src/contexts/ClientContext';
import { HMD_SURVEY_SUBMIT_URL } from 'src/constants/api';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import {
  HMD_POSITIVE_RATING,
  HMD_NEGATIVE_RATING,
  HMD_THUMB_UP,
  HMD_THUMB_DOWN,
  HMD_POSITIVE_RATING_NUMERIC,
  HMD_NEGATIVE_RATING_NUMERIC,
} from 'src/constants/hmdFeedback';
import { InstanceStateData } from '../../types/state/learningModule';

const HMDFeedback = ({
  instance,
}: {
  instance: InstanceStateData
}) => {
  const [bundle] = useBundle(BUNDLE_NAME.HMD_FEEDBACK);
  const formRef = useRef<HTMLFormElement>(null);
  const ratingInputRef = useRef<HTMLInputElement>(null);
  const reasonsInputRef = useRef<HTMLInputElement>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const thumbValueInputRef = useRef<HTMLInputElement>(null);

  const [addContentFeedback] = useAddContentFeedbackMutation();
  const clientId = useClientId();

  // Memoize custom attribute values
  const customAttribute2Value = useMemo(
    () => `instance:${instance.instanceId}`,
    [instance.instanceId],
  );

  const customAttribute3Value = useMemo(
    () => `instanceVersion:${instance.instanceVersion}`,
    [instance.instanceVersion],
  );

  // Helper function to submit form via sendBeacon
  const submitFormViaBeacon = useCallback((rating: string, reasons: string, comment: string, thumbValue: string) => {
    // SAFETY: The form and all inputs are guaranteed to be rendered and refs populated.
    // The control flow ensures this function is only called from user click handlers,
    // which occur after React has committed the render and populated all refs.

    // Update hidden form fields — reason codes go to feedbackComment_1,
    // free-text comment goes to feedbackComment_2 (matching SpeakFeedbackLambdaJS schema)
    ratingInputRef.current!.value = rating;
    reasonsInputRef.current!.value = reasons;
    commentInputRef.current!.value = comment;
    thumbValueInputRef.current!.value = thumbValue;

    // Prepare feedback data for logging
    const feedbackData = {
      moduleId: instance.moduleId,
      instanceId: instance.instanceId,
      instanceVersion: instance.instanceVersion,
      rating,
      thumbValue,
      reasons,
      url: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    // Log feedback submission attempt
    ClientLogger.info('HMDFeedbackSubmission', {
      ...feedbackData,
      feature: 'HMDFeedback',
      submissionMethod: 'sendBeacon',
    });

    // Submit using sendBeacon
    const formData = new FormData(formRef.current!);
    const urlParams = new URLSearchParams();
    formData.forEach((value, key) => {
      urlParams.append(key, value.toString());
    });

    const blob = new Blob([urlParams.toString()], {
      type: 'application/x-www-form-urlencoded',
    });

    // sendBeacon will continue to process even after page navigation begins
    // It returns false if the data couldn't be queued for transfer
    const beaconQueued = navigator.sendBeacon(HMD_SURVEY_SUBMIT_URL, blob);

    if (!beaconQueued) {
      const error = new Error('Failed to queue HMD feedback data for transfer');
      ClientLogger.error(
        ClientLog.BeaconSubmissionFailure,
        error,
        {
          ...feedbackData,
          feature: 'HMDFeedback',
          submissionMethod: 'sendBeacon',
          beaconQueued: false,
          formDataSize: blob.size,
          userAgent: navigator.userAgent,
        },
      );
      return;
    }

    // Log successful queueing
    ClientLogger.info('HMDFeedbackQueued', {
      ...feedbackData,
      feature: 'HMDFeedback',
      beaconQueued: true,
    });
  }, [instance.moduleId, instance.instanceId, instance.instanceVersion]);

  // Positive feedback: immediate API call on thumb up.
  // Dual-write: fire-and-forget NES mutation alongside
  // the beacon so BI can validate the new pipeline without data loss during dial-up.
  const handleOnSelected = useCallback((_event: React.MouseEvent<HTMLElement>, type: ThumbFeedbackType) => {
    if (type !== ThumbFeedbackType.Up) return;

    submitFormViaBeacon(HMD_POSITIVE_RATING, '', '', HMD_THUMB_UP);
    // Dual-write to legacy NES endpoint (fire-and-forget) so BI can validate
    // the Rainforest pipeline during dial-up. The ungated guard at the render
    // boundary ensures this never runs for unauthenticated users.
    addContentFeedback({
      instance,
      createdAt: Date.now(),
      clientId,
      feedback: {
        rating: HMD_POSITIVE_RATING_NUMERIC,
      },
    }).unwrap()
      .catch((err) => ClientLogger.error(ClientLog.APIRequestFailure, err));
  }, [submitFormViaBeacon, addContentFeedback, instance, clientId]);

  // Positive feedback comment: second API call with the comment
  // Only used when Rainforest weblab is active (positive comment step only renders in T1)
  const handlePositiveCommentSubmit = useCallback((comment: string) => {
    submitFormViaBeacon(HMD_POSITIVE_RATING, '', comment, HMD_THUMB_UP);
  }, [submitFormViaBeacon]);

  // Negative feedback: single API call with options + optional comment.
  // Dual-write: fire-and-forget NES mutation alongside
  // the beacon. Free-text comment is beacon-only (NES schema has no comment field).
  const handleOnClicked = useCallback((selectedOptions: FeedbackOptions[], comment?: string) => {
    const reasons = selectedOptions.map((opt) => opt.id).join(', ');

    submitFormViaBeacon(HMD_NEGATIVE_RATING, reasons, comment || '', HMD_THUMB_DOWN);
    // Dual-write to legacy NES endpoint (fire-and-forget). Free-text comment is
    // beacon-only (NES schema has no comment field). Ungated guard at render
    // boundary ensures this never runs for unauthenticated users.
    addContentFeedback({
      instance,
      createdAt: Date.now(),
      clientId,
      feedback: {
        rating: HMD_NEGATIVE_RATING_NUMERIC,
        reasons: selectedOptions.map(({ id }) => ({
          optionKey: id.replace(/-/g, '').slice(0, 10),
          optionValue: id,
        })),
      },
    }).unwrap()
      .catch((err) => ClientLogger.error(ClientLog.APIRequestFailure, err));
  }, [submitFormViaBeacon, addContentFeedback, instance, clientId]);

  // Memoize feedback props to prevent unnecessary re-renders
  const thumbsFeedbackProps = useMemo(() => ({
    title: t(bundle, HMD_FEEDBACK_STRING_ID.THUMB_FEEDBACK_TITLE),
    failureFeedbackMessage: t(bundle, HMD_FEEDBACK_STRING_ID.GENERAL_ERROR_MESSAGE),
    onSelect: handleOnSelected,
  }), [bundle, handleOnSelected]);

  const negativeFeedbackProps = useMemo(() => ({
    title: t(bundle, HMD_FEEDBACK_STRING_ID.NEGATIVE_FEEDBACK_TITLE_TEXT),
    options: [
      {
        id: 'inaccurate-outdated',
        label: t(bundle, HMD_FEEDBACK_STRING_ID.FEEDBACK_NEGATIVE_OPTION_1),
      },
      {
        id: 'did-not-play-properly',
        label: t(bundle, HMD_FEEDBACK_STRING_ID.FEEDBACK_NEGATIVE_OPTION_2),
      },
      {
        id: 'did-not-answer-question',
        label: t(bundle, HMD_FEEDBACK_STRING_ID.FEEDBACK_NEGATIVE_OPTION_3),
      },
      {
        id: 'not-in-my-language',
        label: t(bundle, HMD_FEEDBACK_STRING_ID.FEEDBACK_NEGATIVE_OPTION_4),
      },
      {
        id: 'other',
        label: t(bundle, HMD_FEEDBACK_STRING_ID.FEEDBACK_NEGATIVE_OPTION_5),
      },
    ],
    noOptionSelectedErrMessage: t(bundle, HMD_FEEDBACK_STRING_ID.NO_FEEDBACK_PROVIDED_ERROR_MESSAGE),
    failureFeedbackMessage: t(bundle, HMD_FEEDBACK_STRING_ID.GENERAL_ERROR_MESSAGE),
    submitButton: {
      label: t(bundle, HMD_FEEDBACK_STRING_ID.SUBMIT_TEXT),
      onClick: handleOnClicked,
    },
    showComment: true,
    commentProps: {
      placeholder: t(bundle, HMD_FEEDBACK_STRING_ID.NEGATIVE_COMMENT_PLACEHOLDER),
      piiWarning: t(bundle, HMD_FEEDBACK_STRING_ID.COMMENT_PII_WARNING),
      piiDisclaimerLabel: t(bundle, HMD_FEEDBACK_STRING_ID.PII_DISCLAIMER_LABEL),
      piiDisclaimerDescription: t(bundle, HMD_FEEDBACK_STRING_ID.PII_DISCLAIMER_DESCRIPTION),
      charactersRemainingText: t(bundle, HMD_FEEDBACK_STRING_ID.CHARACTERS_REMAINING),
    },
  }), [bundle, handleOnClicked]);

  // Positive feedback comment props
  const positiveFeedbackCommentProps = useMemo(() => {
    return {
      title: t(bundle, HMD_FEEDBACK_STRING_ID.POSITIVE_FEEDBACK_COMMENT_TITLE),
      successFeedbackMessage: t(bundle, HMD_FEEDBACK_STRING_ID.SUCCESS_FEEDBACK_TEXT),
      failureFeedbackMessage: t(bundle, HMD_FEEDBACK_STRING_ID.GENERAL_ERROR_MESSAGE),
      submitButton: {
        label: t(bundle, HMD_FEEDBACK_STRING_ID.SUBMIT_TEXT),
        onClick: handlePositiveCommentSubmit,
      },
      commentProps: {
        placeholder: t(bundle, HMD_FEEDBACK_STRING_ID.COMMENT_PLACEHOLDER),
        piiWarning: t(bundle, HMD_FEEDBACK_STRING_ID.COMMENT_PII_WARNING),
        piiDisclaimerLabel: t(bundle, HMD_FEEDBACK_STRING_ID.PII_DISCLAIMER_LABEL),
        piiDisclaimerDescription: t(bundle, HMD_FEEDBACK_STRING_ID.PII_DISCLAIMER_DESCRIPTION),
        charactersRemainingText: t(bundle, HMD_FEEDBACK_STRING_ID.CHARACTERS_REMAINING),
      },
    };
  }, [bundle, handlePositiveCommentSubmit]);

  const feedbackSubmittedProps = useMemo(() => ({
    successFeedbackMessage: t(bundle, HMD_FEEDBACK_STRING_ID.SUCCESS_FEEDBACK_TEXT),
    successFeedbackAgainMessage: t(bundle, HMD_FEEDBACK_STRING_ID.SUCCESS_FEEDBACK_AGAIN_TEXT),
  }), [bundle]);

  // TODO: Remove this guard once SP_SERVICES_SELLER_UNIVERSITY_RAINFOREST_HMD_FEEDBACK_1368028
  // is fully dialed up and cleaned up. At that point ungated users can submit feedback via
  // sendBeacon without auth and this restriction should be revisited.
  if (clientId === UNGATED_CLIENT_ID) return null;

  return (
    <form id="su-hmd-form" ref={formRef} aria-label="Content feedback form">
      {/* Hidden inputs for Rainforest schema fields */}
      <input type="hidden" name="rating_1" ref={ratingInputRef} value="0" />
      <input type="hidden" name="hmdID" value="su-content-feedback-v1" />
      <input type="hidden" name="url" value={window.location.pathname} />
      <input type="hidden" name="requestID" value={(window as any).ue_id} />
      {/* Field names must match SpeakFeedbackLambdaJS URL param keys (hmdFeedback.ts) */}
      {/* feedbackComment_1: reason codes → feedback_v8.comment_1 */}
      <input type="hidden" name="feedbackComment_1" ref={reasonsInputRef} value="" />
      {/* feedbackComment_2: free-text comment → feedback_v8.comment_2 */}
      <input type="hidden" name="feedbackComment_2" ref={commentInputRef} value="" />
      {/* Custom attributes - will be read by custom submit handler */}
      <input
        type="hidden"
        name="customAttribute1Value"
        value={`module:${instance.moduleId}`}
      />
      <input
        type="hidden"
        name="customAttribute2Value"
        value={customAttribute2Value}
      />
      <input
        type="hidden"
        name="customAttribute3Value"
        value={customAttribute3Value}
      />
      <input type="hidden" name="thumb_value" ref={thumbValueInputRef} value="up" />

      <SUHMDFeedback
        key={instance.instanceId}
        className="p-su-space-00"
        thumbsFeedbackProps={thumbsFeedbackProps}
        negativeFeedbackProps={negativeFeedbackProps}
        positiveFeedbackCommentProps={positiveFeedbackCommentProps}
        feedbackSubmittedProps={feedbackSubmittedProps}
      />
    </form>
  );
};

export default HMDFeedback;
